import { useState } from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { Link } from 'react-router-dom'
import { COLOR_PRIMARY, COLOR_SKY, NAVBAR_BG } from '../theme'

interface FancyLightButtonProps {
    to?: string
    href?: string
    color?: string
    onClick?: () => void
    endIcon?: React.ReactNode
    transparent?: boolean
    noBorder?: boolean
    textColor?: string
    fullWidth?: boolean
    sx?: SxProps<Theme>
    children: React.ReactNode
}

// MUI canonical merge: array form so callers can pass an object, an array, or a theme callback.
const mergeSx = (base: SxProps<Theme>, extra?: SxProps<Theme>): SxProps<Theme> =>
    extra ? [base, ...(Array.isArray(extra) ? extra : [extra])] : base

export function FancyLightButton({ to, href, color = COLOR_PRIMARY, onClick, endIcon, transparent, noBorder, textColor, children, fullWidth, sx: sxOverride }: FancyLightButtonProps) {
    const [hover, setHover] = useState(false)

    const handleMouseEnter = () => setHover(true)

    const commonSx = {
        ...(fullWidth && { width: '100%' }),
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 1.25,
        border: noBorder ? 'none' : `2px solid ${color}`,
        borderRadius: '8px',
        backgroundColor: transparent ? 'transparent' : 'white',
        color: hover ? COLOR_SKY : (textColor ?? NAVBAR_BG),
        fontWeight: 600,
        fontSize: '15px',
        lineHeight: 1.5,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.3s ease',
        letterSpacing: '0.3px',
        userSelect: 'none',
    }

    const label = (
        <Box component="span" className="fancylink" sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {children}
            {endIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{endIcon}</Box>}
        </Box>
    )

    if (href) {
        return (
            <Box component="a" href={href} className="fancybutton" onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)} sx={mergeSx(commonSx, sxOverride)}>
                {label}
            </Box>
        )
    }

    if (to) {
        return (
            <Box component={Link} to={to} className="fancybutton" onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)} sx={mergeSx(commonSx, sxOverride)}>
                {label}
            </Box>
        )
    }

    return (
        <Box component="button" className="fancybutton" onMouseEnter={handleMouseEnter} onMouseLeave={() => setHover(false)} onClick={onClick}
            sx={mergeSx({ ...commonSx, fontFamily: 'inherit' }, sxOverride)}>
            {label}
        </Box>
    )
}
